// The coin shop (spec FE-32, BE-26): loads, slogans, paint finishes, horns, Owa scenes and meeting
// stickers bought with coins, what you have in use, and coin packs bought through Lagos Life.
// Prices, pack sizes, what you own and what you use all come from the server.
import { type MouseEvent, useCallback, useEffect, useState } from 'react';
import { errorText } from '../App';
import { api, type ShopKind, type ShopState } from '../lib/api';
import { parkNaira } from '../lib/money';
import { playHorn } from '../lib/sound';
import { useUi } from '../store/ui';
import { S } from '../strings';
import { GetCoins } from '../ui/GetCoins';
import { RedeemCode } from '../ui/RedeemCode';
import { ScreenTop } from '../ui/ScreenTop';
import { Html, W } from '../ui/wahala';

const KINDS: ShopKind[] = ['load', 'finish', 'slogan', 'horn', 'owa', 'sticker', 'power'];

/** The load and finish the player has in use, for the preview keke (the tile's own item wins). */
function lookOf(shop: ShopState, kind: ShopKind): { load?: string; finish?: string } {
  const item = (slot: 'load' | 'finish') => shop.items.find((i) => i.id === shop.equipped[slot])?.item;
  const load = kind === 'load' ? undefined : item('load');
  const finish = kind === 'finish' ? undefined : item('finish');
  return { ...(load ? { load } : {}), ...(finish ? { finish } : {}) };
}

export function Shop() {
  const set = useUi((s) => s.set);
  const [shop, setShop] = useState<ShopState | null>(null);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState<ShopKind>('load');

  const load = useCallback(async () => {
    try {
      const s = await api.shop();
      setShop(s);
      set({ coins: s.coins });
    } catch (e) {
      set({ notice: errorText(e) });
    }
  }, [set]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onShopClick(e: MouseEvent<HTMLDivElement>) {
    const el = e.target as HTMLElement;
    // hearing a horn is free and needs no server
    const horn = el.closest('[data-horn]')?.getAttribute('data-horn');
    if (horn) {
      playHorn(horn);
      return;
    }
    const buy = el.closest('[data-item]')?.getAttribute('data-item');
    const use = el.closest('[data-equip]')?.getAttribute('data-equip');
    if ((!buy && !use) || busy) return;
    setBusy(true);
    set({ notice: null });
    try {
      if (buy) {
        await api.buy(buy);
        set({ notice: S.shop.bought, noticeTone: 'done' });
      } else if (use) {
        await api.equip({ itemId: use });
        set({ notice: S.shop.using, noticeTone: 'done' });
      }
      await load();
    } catch (err) {
      set({ notice: errorText(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="screen shop-screen" data-theme="day">
      <ScreenTop title={S.menu.shop} />
      {!shop && <p className="boot">{S.shop.loading}</p>}
      {shop && shop.debt > 0 && <p className="card muted">{S.shop.debt(parkNaira(shop.debt))}</p>}
      {shop && (
        <fieldset className="chips">
          <legend className="sr-only">{S.shop.kindsLabel}</legend>
          {KINDS.map((k) => (
            <button
              key={k}
              type="button"
              aria-pressed={kind === k}
              className={`chip${kind === k ? ' is-on' : ''}`}
              onClick={() => setKind(k)}
            >
              {S.shop.kinds[k]}
            </button>
          ))}
        </fieldset>
      )}
      {shop && (
        <Html
          onClick={(e) => void onShopClick(e)}
          html={W().shop({
            title: S.shop.kinds[kind] ?? S.shop.title,
            coins: shop.coins,
            player: 'danfo',
            disabled: busy || shop.debt > 0,
            note: S.shop.note,
            items: shop.items
              .filter((i) => i.kind === kind)
              .map((i) => ({
                id: i.id,
                kind: i.kind,
                item: i.item,
                name: i.name,
                price: i.price,
                owned: i.owned,
                equipped: i.equipped,
                // show each item on the keke as it is now: their own load and paint
                ...lookOf(shop, i.kind),
              })),
          })}
        />
      )}

      {shop && kind === 'sticker' && (
        <section className="card ticket" aria-labelledby="ticket-title">
          {/* the name, how many you have and Buy one on one line; what it does under it */}
          <div className="ticket__head">
            <h2 id="ticket-title" className="label">
              {S.tickets.title}
            </h2>
            <span className="muted small">{S.tickets.have(shop.tickets)}</span>
            <button
              type="button"
              className="wh-btn wh-btn--carry wh-btn--sm"
              disabled={busy || shop.debt > 0 || shop.coins < shop.ticketPrice}
              onClick={async () => {
                setBusy(true);
                try {
                  await api.buyTicket(crypto.randomUUID());
                  set({ notice: S.tickets.bought, noticeTone: 'done' });
                  await load();
                } catch (e) {
                  set({ notice: errorText(e) });
                } finally {
                  setBusy(false);
                }
              }}
            >
              {S.tickets.buy}
            </button>
          </div>
          <p className="muted small">{S.tickets.line(parkNaira(shop.ticketPrice))}</p>
        </section>
      )}
      {/* a promo code from the park office: park money (and maybe the Route Pass) once per player */}
      {shop && <RedeemCode onRedeemed={() => void load()} />}
      {/* coin packs: the same four-across strip as the home page */}
      {shop && <GetCoins note onBought={() => void load()} />}
    </main>
  );
}
